<template>
  <div>

    <!-- Toast component and its position -->
    <Toast position="top-right" />
    
    <!-- Navigation Component -->
    <navigation />
    
    <!-- Toolbar component used to display all the helping action items -->
    <Toolbar class="tool-bar-styling p-mb-4">

      <template slot="left">
        <Button label="New" icon="pi pi-plus" class="p-button-info p-mr-2" v-tooltip="'Click to add new liability'" @click="openNew" v-if="isFactoryTeamMember || isProductionTeamMember"/>
        <Button label="Clone" icon="pi pi-copy" class="p-button-info p-mr-2" v-tooltip="'Click to clone selected liabilities'" :disabled="!selectedProducts || !selectedProducts.length" @click="cloneRowsHandler" v-if="isFactoryTeamMember || isProductionTeamMember"/>
        <Button label="Save Cloned Entries" icon="pi pi-save" v-tooltip="'Click to save cloned liabilities'" class="p-button-info p-mr-2" :disabled="!enableSaveClonedCTA" @click="saveClonedLiabilities" v-if="isFactoryTeamMember || isProductionTeamMember"/>
        <Button label="Bulk Edit" icon="pi pi-pencil" v-tooltip="'Click to eidt selected liabilities'" class="p-button-info p-mr-2" :disabled="!selectedProducts || !selectedProducts.length" @click="bulkEditClickHandler" />
      </template>

      <template slot="right">
        <FileUpload v-if="isFactoryTeamMember || isProductionTeamMember" v-tooltip="'Click to import liabilities'" class="p-mr-2" @upload="onUpload" mode="basic" ref="file" name="file" :customUpload="true" :maxFileSize="1000000" @uploader="onFileUpload" :auto="true" chooseLabel="Import" />
        <Button label="Export" icon="pi pi-upload" v-tooltip="'Click to export liabilities'" class="p-button-help" @click="exportCSV($event)"  />
      </template>

    </Toolbar>

    <DataTable v-if="!isLoaded" :paginator="true" :rows="10" :value="skeletonData">
      <Column field="date_incurred" header="Date Incurred" :sortable="true" headerStyle="width: 170px">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>
      <Column field="vendor" header="Vendor" :sortable="true" headerStyle="width: 200px">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>
      <Column field="liability_reason" header="Liability Reason" :sortable="true" headerStyle="width: 300px">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>
      <Column field="status" header="Status" :sortable="true" headerStyle="width: 220px">
        <template #body>
          <Skeleton></Skeleton>
        </template>
      </Column>
	  </DataTable>

    <div class="data-table-parent-container">
      <DataTable ref="dt" v-if="isLoaded" :value="masterLiabilityData" dataKey="id" :scrollable="true" :rowClass="rowClass"
        :paginator="true" :rows="10" :filters="filters" :selection.sync="selectedProducts"
        class="liability-chart" paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" :rowsPerPageOptions="[10,20,30]"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Liabilities">
          <template #header>
            <div>
              <div class="p-grid p-ai-center vertical-container">
                <div class="p-col-2">Liabilities<br>
                  <span class="liability-count">Total {{totalLiabilityCount}} Liabilities</span>
                </div>
                <div class="p-offset-8 p-col-2">
                  <span class="p-input-icon-left">
                    <i class="pi pi-search" />
                    <InputText placeholder="Global Search" v-model="filters['global']"/>
                  </span>
                </div>
              </div>
            </div>
          </template>

          <Column :exportable="false" headerStyle="width: 4rem">
            <template #body="slotProps">
              <Button icon="pi pi-fw pi-pencil" class="p-button-rounded p-button-success p-mr-2" @click="editProduct(slotProps.data)" />
            </template>
          </Column>

          <Column selectionMode="multiple" headerStyle="width: 3rem" :exportable="false"></Column>

          <Column :exportable="false" headerStyle="width: 70px" header="View History">
            <template #body="slotProps">
              <Button icon="pi pi-fw pi-user" class="p-button-rounded p-button-warning p-mr-2" @click="showHistory(slotProps.data)" />
            </template>
          </Column>

          <Column field="id" header="Liability ID" :sortable="true" headerStyle="width: 170px"></Column>

          <Column field="date_incurred" header="Date Incurred" :sortable="true" headerStyle="width: 170px">
            <template #filter>
              <InputText type="text" v-model="filters['date_incurred']" class="p-column-filter" placeholder="Search by Date"/>
            </template>
          </Column>

          <Column field="vendor" header="Vendor" :sortable="true" headerStyle="width: 200px">
            <template #filter>
                <InputText type="text" v-model="filters['vendor']" class="p-column-filter" placeholder="Search by Vendor"/>
            </template>
          </Column>
        
          <Column field="liability_reason" header="Liability Reason" :sortable="true" headerStyle="width: 300px"></Column>
        
          <Column field="status" header="Status" :sortable="true" headerStyle="width: 220px">
            <template #filter>
              <InputText type="text" v-model="filters['status']" class="p-column-filter" placeholder="Search by Status"/>
            </template>
          </Column>
        
          <Column field="division" header="Division" :sortable="true" headerStyle="width: 200px">
            <template #filter>
              <InputText type="text" v-model="filters['division']" class="p-column-filter" placeholder="Search by Division"/>
            </template>
          </Column>
          
          <Column :exportable="false" field="comment" header="Comment" headerStyle="width: 450px"> 
            <template #body="slotProps"><p v-html="populateCommentMarkup(slotProps.data.comment)"></p></template>
          </Column>
        
          <Column field="season_name" header="Season Name" :sortable="true" headerStyle="width: 150px" filterMatchMode="in">
            <template #filter>
                <MultiSelect v-model="filters['season_name']" :options="liabilitySeasonName" optionLabel="name" optionValue="name" placeholder="All" class="p-column-filter multi-option">
                    <template #option="slotProps">
                        <div class="p-multiselect-representative-option">
                            <span class="image-text">{{slotProps.option.name}}</span>
                        </div>
                    </template>
                </MultiSelect>
            </template>
          </Column>
        
          <Column field="season_year" header="Season Year" :sortable="true" headerStyle="width: 150px">
            <template #filter>
                <InputText type="text" v-model="filters['season_year']" class="p-column-filter" placeholder="Search by Year"/>
            </template>
          </Column>
        
          <Column field="supplier" header="Supplier" :sortable="true" headerStyle="width: 250px">
            <template #filter>
                <InputText type="text" v-model="filters['supplier']" class="p-column-filter" placeholder="Search by Supplier"/>
            </template>
          </Column>

          <Column field="collection" header="Collection" :sortable="true" headerStyle="width: 250px">
            <template #filter>
                <InputText type="text" v-model="filters['collection']" class="p-column-filter" placeholder="Search by Collection"/>
            </template>
          </Column>
        
          <Column field="style" header="Style" :sortable="true" headerStyle="width: 250px">
            <template #filter>
                <InputText type="text" v-model="filters['style']" class="p-column-filter" placeholder="Search by Style"/>
            </template>
          </Column>
        
          <Column field="style_description" header="Style Description" :sortable="true" headerStyle="width: 350px"></Column>
          
          <Column field="greige_or_dyed" header="Greige Or Dyed" :sortable="true" headerStyle="width: 190px">
            <template #filter>
              <Dropdown v-model="filters['greige_or_dyed']" :options="liabilityColorPattern" optionLabel="name" optionValue="name" placeholder="Select a Color Pattern" class="liability-chart p-column-filter" :showClear="true">
                <template #option="slotProps">
                  <span>{{slotProps.option.name}}</span>
                </template>
              </Dropdown>
            </template>
          </Column>
        
          <Column field="liability_location" header="Liability Location" :sortable="true" headerStyle="width: 220px">
            <template #filter>
                <Dropdown v-model="filters['liability_location']" :options="liabilityLocation" optionLabel="name" optionValue="name" placeholder="Select a Location" class="liability-chart p-column-filter" :showClear="true">
                  <template #option="slotProps">
                    <span>{{slotProps.option.name}}</span>
                  </template>
                </Dropdown>
            </template>
          </Column>
        
          <Column field="article" header="Article #" :sortable="true" headerStyle="width: 220px">
            <template #filter>
              <InputText type="text" v-model="filters['article']" class="p-column-filter" placeholder="Search by Article #"/>
            </template>
          </Column>
        
          <Column field="fabric_name_and_description" header="Fabric Name & Description" :sortable="true" headerStyle="width: 350px"></Column>
          
          <Column field="trim_description_and_article" header="Trim Description & Article" :sortable="true" headerStyle="width: 300px"></Column>
          
          <Column field="color" header="Color" :sortable="true" headerStyle="width: 210px">
            <template #filter>
              <InputText type="text" v-model="filters['color']" class="p-column-filter" placeholder="Search by Article #"/>
            </template>
          </Column>
        
          <Column field="quantity" header="Quantity" :sortable="true" headerStyle="width: 190px">
            <template #filter>
              <InputText type="text" v-model="filters['quantity']" class="p-column-filter" placeholder="Search by Article #"/>
            </template>
          </Column>
        
          <Column field="uom" header="UOM" :sortable="true" headerStyle="width: 170px">
            <template #filter>
              <Dropdown v-model="filters['uom']" :options="liabilityUOM" optionLabel="name" optionValue="name" placeholder="Select a UOM" class="liability-chart p-column-filter" :showClear="true">
                <template #option="slotProps">
                  <span>{{slotProps.option.name}}</span>
                </template>
              </Dropdown>
            </template>
          </Column>
        
          <Column field="fob_unit_cost" header="Fob Unit Cost" :sortable="true" headerStyle="width: 190px">
            <template #filter>
              <InputText type="text" v-model="filters['fob_unit_cost']" class="p-column-filter" placeholder="Search by Article #"/>
            </template>
          </Column>
        
          <Column field="shelf_life" header="Shelf Life" :sortable="true" headerStyle="width: 220px">
            <template #filter>
              <InputText type="text" v-model="filters['shelf_life']" class="p-column-filter" placeholder="Search by Shelf Life"/>
            </template>
          </Column>
        
          <Column field="total_liability" header="Total Liability" :sortable="true" headerStyle="width: 250px">
            <template #filter>
              <InputText type="total_liability" v-model="filters['total_liability']" class="p-column-filter" placeholder="Search Total Liability"/>
            </template>
          </Column>
        
          <Column field="estimated_garment_units" header="Estimated Garment Units" :sortable="true" headerStyle="width: 300px">
            <template #filter>
              <InputText type="text" v-model="filters['estimated_garment_units']" class="p-column-filter" placeholder="Search Estimated Garment Units"/>
            </template>
          </Column>
        
          <Column field="deposit" header="Deposit" :sortable="true" headerStyle="width: 200px">
            <template #filter>
              <Dropdown v-model="filters['deposit']" :options="liabilityDeposit" optionLabel="name" optionValue="name" placeholder="Select a Deposit" class="liability-chart p-column-filter" :showClear="true">
                <template #option="slotProps">
                  <span>{{slotProps.option.name}}</span>
                </template>
              </Dropdown>
            </template>
          </Column>
        
          <Column field="po" header="PO #" :sortable="true" headerStyle="width: 200px">
            <template #filter>
              <InputText type="text" v-model="filters['po']" class="p-column-filter" placeholder="Search by PO #"/>
            </template>
          </Column>
          
          <Column field="date_invoice_received" header="Date Invoice Received" :sortable="true" headerStyle="width: 250px">
            <template #filter>
              <InputText type="text" v-model="filters['date_invoice_received']" class="p-column-filter" placeholder="Search by Received Invoice Date"/>
            </template>
          </Column>
        
          <Column field="invoice" header="Invoice" :sortable="true" headerStyle="width: 250px">
            <template #filter>
              <InputText type="text" v-model="filters['invoice']" class="p-column-filter" placeholder="Search by Received Invoice Date"/>
            </template>
          </Column>

          <Column field="approved" header="Validated by production" :sortable="true" headerStyle="width: 300px">
            <template #filter>
              <Dropdown v-model="filters['approved']" :options="approvedOptions" optionLabel="name" optionValue="name" placeholder="Select if Validated" class="liability-chart p-column-filter" :showClear="true">
                <template #option="slotProps">
                  <span>{{slotProps.option.name}}</span>
                </template>
              </Dropdown>
            </template>
          </Column>
        
          <Column field="initial_approver_for_invoice_payment" header="Initial Approver for Invoice Payment" :sortable="true" headerStyle="width: 300px">
            <template #filter>
              <InputText type="text" v-model="filters['initial_approver_for_invoice_payment']" class="p-column-filter" placeholder="Search by Approver"/>
            </template>
          </Column>

          <Column field="final_approver_for_invoice_payment" header="Final Approver for Invoice Payment" :sortable="true" headerStyle="width: 300px">
            <template #filter>
              <InputText type="text" v-model="filters['final_approver_for_invoice_payment']" class="p-column-filter" placeholder="Search by Approver"/>
            </template>
          </Column>
        
          <Column field="date_invoice_paid" header="Date Invoice Paid" :sortable="true" headerStyle="width: 180px">
            <template #filter>
              <InputText type="text" v-model="filters['date_invoice_paid']" class="p-column-filter" placeholder="Search by Date Invoice Paid"/>
            </template>
          </Column>
        
          <Column field="original_units" header="Original Units" :sortable="true" headerStyle="width: 200px">
            <template #filter>
              <InputText type="text" v-model="filters['original_units']" class="p-column-filter" placeholder="Search by Original Units"/>
            </template>
          </Column>
        
          <Column field="original_total_liability" header="Original Total Liability" :sortable="true" headerStyle="width: 250px">
            <template #filter>
              <InputText type="text" v-model="filters['original_total_liability']" class="p-column-filter" placeholder="Search by Total Liability"/>
            </template>
          </Column>
        
          <Column field="date_closed" header="Date Closed" :sortable="true" headerStyle="width: 180px">
            <template #filter>
              <InputText type="text" v-model="filters['date_closed']" class="p-column-filter" placeholder="Search by Closed Date"/>
            </template>
          </Column>
        
          <Column field="closed" header="Closed" :sortable="true" headerStyle="width: 180px">
            <template #filter>
                <Dropdown v-model="filters['closed']" :options="liabilityClosedStatus" optionLabel="name" optionValue="name" placeholder="Select Closed" class="liability-chart p-column-filter" :showClear="true">
                  <template #option="slotProps">
                    <span>{{slotProps.option.name}}</span>
                  </template>
                </Dropdown>
            </template>
          </Column>
        </DataTable>
      </div>
    
      <Dialog v-if="isLoaded" :visible="productDialog" :closable="false" :style="{width: '1250px', height:'850px'}" header="Liability Details" :modal="true" class="p-fluid custom-dialog">
      
        <div class="p-formgrid p-grid">  
          <div class="p-field p-col">
            <label>Date Incurred</label>
            <InputText id="date_incurred" placeholder="MM-DD-YYYY" v-model.trim="masterLiabilityInfo.date_incurred" disabled="true" />
            <small class="p-invalid" v-if="submitted && !validDate">Enter a Valid Date.</small>
          </div>
        
          <div class="p-field p-col" v-if="isEditEntryDialoge">
            <label>Vendor*</label>
            <InputText id="vendor" v-model.trim="masterLiabilityInfo.vendor"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.vendor">Vendor is required.</small>
          </div>

          <div class="p-field p-col" v-else>
            <label>Vendor*</label>
            <InputText id="vendor" v-model.trim="masterLiabilityInfo.vendor"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.vendor">Vendor is required.</small>
          </div>

          <div class="p-field p-col">
            <label>Status</label>
            <Dropdown :disabled="!isProductionTeamMember" v-model="masterLiabilityInfo.status" :options="liabilityStatus" optionLabel="name" optionValue="value" placeholder="Select a Status">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
          </div>
        </div>

        <div class="p-field p-col">
          <label>Liability Reason</label><br>
          <Dropdown v-model="masterLiabilityInfo.liability_reason" :options="liabilityReason" optionLabel="name" optionValue="value" placeholder="Select a Liability Reason" class="liability-chart p-column-filter">
            <template #option="slotProps">
              <span>{{slotProps.option.name}}</span>
            </template>
          </Dropdown>
        </div>

        <div class="p-field p-col">
          <label>Comment</label><br>
          <p v-if="masterLiabilityInfo.comment" v-html="populateCommentMarkup(masterLiabilityInfo.comment)"></p>
          <Textarea id="liability_comment" rows="3" cols="104" />
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <label for="price">Division*</label>
            <Dropdown :disabled="!isFactoryTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.division" :options="divisionOptions" optionLabel="name" optionValue="value" placeholder="Select a Division">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.division">Division is required.</small>
          </div> 

          <div class="p-field p-col">
            <label>Season Name*</label>
            <Dropdown :disabled="!isFactoryTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.season_name" :options="liabilitySeasonName" optionLabel="name" optionValue="value" placeholder="Select a Season Name">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.season_name">Season Name is required.</small>
          </div>
        
          <div class="p-field p-col">
            <label for="price">Season Year*</label>
            <InputNumber :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="season_year" v-model.trim="masterLiabilityInfo.season_year" :useGrouping="false"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.season_year">Season Year is required.</small>
          </div>
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <label>Supplier*</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="supplier" v-model.trim="masterLiabilityInfo.supplier"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.supplier">Supplier is required.</small>
          </div>

          <div class="p-field p-col">
            <label>Collection*</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="collection" v-model.trim="masterLiabilityInfo.collection"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.collection">Collection is required.</small>
          </div>
          
          <div class="p-field p-col">
            <label>Style</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="style" v-model.trim="masterLiabilityInfo.style"/>
          </div>

          <div class="p-field p-col">
            <label>Greige Or Dyed*</label>
            <Dropdown :disabled="!isFactoryTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.greige_or_dyed" :options="liabilityColorPattern" optionLabel="name" optionValue="value" placeholder="Select a Pattern">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.greige_or_dyed">Pattern is required.</small>
          </div>
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <label for="price">Liability Location*</label>
            <Dropdown :disabled="!isFactoryTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.liability_location" :options="liabilityLocation" optionLabel="name" optionValue="value" placeholder="Select a Location">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.liability_location">Liability Location is required.</small>
          </div>
          
          <div class="p-field p-col">
            <label>Article# *</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="article" v-model.trim="masterLiabilityInfo.article"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.article">Article Number is required.</small>
          </div>

          <div class="p-field p-col">
            <label>Color*</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="color" v-model.trim="masterLiabilityInfo.color"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.color">Color is required.</small>
          </div>
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <label>Style Description</label><br>
            <Textarea :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="style_description" v-model.trim="masterLiabilityInfo.style_description" required="true" rows="6" cols="56" />
          </div>

          <div class="p-field p-col">
            <label>Fabric Name & Fabric Description</label><br>
            <Textarea :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="fabric_name_and_description" v-model.trim="masterLiabilityInfo.fabric_name_and_description" required="true" rows="6" cols="56" />
          </div>
        </div>

        <div class="p-field p-col">
          <label>Trim Description & Article #</label><br>
          <Textarea :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="trim_description_and_article" v-model.trim="masterLiabilityInfo.trim_description_and_article" required="true" rows="6" cols="104" />
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <label>PO# *</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="po" v-model.trim="masterLiabilityInfo.po"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.po">PO is required.</small>
          </div>

          <div class="p-field p-col">
            <label>Quantity *</label>
            <InputNumber :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="quantity" type="text" pattern="[0-9.,]+" v-model.trim="masterLiabilityInfo.quantity"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.quantity">Quantity is required.</small>
          </div>
          
          <div class="p-field p-col">
            <label>UOM*</label>
            <Dropdown :disabled="!isFactoryTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.uom" :options="liabilityUOM" optionLabel="name" optionValue="value" placeholder="Select a Unit Of Measurement">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.uom">UOM is required.</small>
          </div>
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <label>FOB Unit Cost*</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" type="number" id="fob_unit_cost" v-model.trim="masterLiabilityInfo.fob_unit_cost"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.fob_unit_cost">FOB Unit Cost is required.</small>
          </div>
          
          <div class="p-field p-col">
            <label>Shelf Life*</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="shelf_life" v-model.trim="masterLiabilityInfo.shelf_life" placeholder="MM-DD-YYYY"/>
            <small class="p-invalid" v-if="submitted && !validDate">Enter a Valid Date.</small>
          </div>

          <div class="p-field p-col">
            <label>Total Liability</label>
            <InputText :disabled=true id="total_liability" v-model.trim="masterLiabilityInfo.total_liability"/>
          </div>
        </div>

        <div class="p-formgrid p-grid">
          <div class="p-field p-col">
            <label>Estimated Garment Units*</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="estimated_garment_units" v-model.trim="masterLiabilityInfo.estimated_garment_units"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.estimated_garment_units">Estimated Garment Units is required.</small>
          </div>

          <div class="p-field p-col">   
            <label>Yes/No Deposit*</label>
            <Dropdown :disabled="!isFactoryTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.deposit" :options="liabilityDeposit" optionLabel="name" optionValue="value" placeholder="Select a Value">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.deposit">Deposit is required.</small>
          </div>

          <div class="p-field p-col">   
            <label>Validated by production?*</label>
            <Dropdown :disabled="!isFactoryTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.approved" :options="liabilityDeposit" optionLabel="name" optionValue="value" placeholder="Select a Value">
              <template #value="slotProps">
                <div class="p-dropdown-car-value" v-if="slotProps.value">
                  <span>{{slotProps.value}}</span>
                </div>
                <span v-else>
                  {{slotProps.placeholder}}
                </span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{slotProps.option.name}}</span>
                </div>
              </template>
            </Dropdown>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.approved">Field is required.</small>
          </div>
          
          <div class="p-field p-col">
            <label>Original Units*</label>
            <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="original_units" v-model.trim="masterLiabilityInfo.original_units"/>
            <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.original_units">Original Units is required.</small>
          </div>
        </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label>Invoice #</label>
          <InputText :disabled="!isFinanceTeamMember && !isProductionTeamMember" id="invoice" v-model.trim="masterLiabilityInfo.invoice"/>
        </div>
        
        <div class="p-field p-col">
          <label>Date Invoice received</label>
          <InputText :disabled="!isFinanceTeamMember" id="date_invoice_received" v-model.trim="masterLiabilityInfo.date_invoice_received" placeholder="MM-DD-YYYY"/>
        </div>

        <div class="p-field p-col">
          <label>Initial Approver for Invoice Payment</label>
          <Dropdown :disabled="!isFinanceTeamMember && !isProductionTeamMember" v-model="masterLiabilityInfo.initial_approver_for_invoice_payment" :options="liabilityInvoiceApprover" optionLabel="name" optionValue="value" placeholder="Select a Value">
            <template #value="slotProps">
              <div class="p-dropdown-car-value" v-if="slotProps.value">
                <span>{{slotProps.value}}</span>
              </div>
              <span v-else>
                {{slotProps.placeholder}}
              </span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{slotProps.option.name}}</span>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label>Final Approver for Invoice Payment</label>
          <InputText :disabled="!isFinanceTeamMember && !isProductionTeamMember" id="final_approver_for_invoice_payment" v-model.trim="masterLiabilityInfo.final_approver_for_invoice_payment"/>
        </div>

        <div class="p-field p-col">
          <label>Date Invoice Paid</label>
          <InputText :disabled="!isFinanceTeamMember && !isProductionTeamMember" id="date_invoice_paid" v-model.trim="masterLiabilityInfo.date_invoice_paid" placeholder="MM-DD-YYYY"/>
          <small class="p-invalid" v-if="submitted && !validDate">Enter a Valid Date.</small>
        </div>

        <div class="p-field p-col">
          <label>Original Total Liability*</label>
          <InputText :disabled="!isFactoryTeamMember && !isProductionTeamMember" id="original_total_liability" v-model.trim="masterLiabilityInfo.original_total_liability"/>
          <small class="p-invalid" v-if="submitted && !masterLiabilityInfo.original_total_liability">Original Total Liability is required.</small>
        </div>
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label>Date Closed</label>
          <InputText :disabled="true" id="date_closed" placeholder="MM-DD-YYYY" v-model.trim="masterLiabilityInfo.date_closed"/>
        </div>

        <div class="p-field p-col">
          <label>Closed</label>
          <Dropdown :disabled="!isProductionTeamMember" @change="liabilityClosedStatusChanged" v-model="masterLiabilityInfo.closed" :options="liabilityClosedStatus" optionLabel="name" optionValue="value" placeholder="Select a Value">
            <template #value="slotProps">
              <div class="p-dropdown-car-value" v-if="slotProps.value">
                <span>{{slotProps.value}}</span>
              </div>
              <span v-else>
                {{slotProps.placeholder}}
              </span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{slotProps.option.name}}</span>
              </div>
            </template>
          </Dropdown>
          <!-- <Dropdown v-model="masterLiabilityInfo.closed" :options="liabilityClosedStatus" optionLabel="name" placeholder="Select a Season"  /> -->
        </div>
      </div>

      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="hideDialog"/>
        <Button label="Save" icon="pi pi-check" class="p-button-text" @click="saveProduct" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Toolbar from "primevue/toolbar";
import FileUpload from "primevue/fileupload";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import http from "../../http-common";
import Textarea from 'primevue/textarea';
import MultiSelect from 'primevue/multiselect';
import Toast from 'primevue/toast';
import Tooltip from 'primevue/tooltip';
import navigation from "../../components/helperComponents/menu";
import moment from 'moment';
import Skeleton from 'primevue/skeleton';
export default {
  name: "MasterLiabilityComponent",
  components: {
    navigation,
    Textarea,
    Button,
    Dialog,
    FileUpload,
    Toolbar,
    DataTable,
    Column,
    Dropdown,
    InputText,
    InputNumber,
    MultiSelect,
    Toast,
    Skeleton
  },
  directives: {
    'tooltip': Tooltip
  },
  data() {
    return {
      file: '',
      masterLiabilityData: [],
      masterLiabilityCommentData: [],
      isLoaded: false,
      productDialog: false,
      editLog: false,
      masterLiabilityInfo: {},
      editLogInfo: {},
      clonedLiabilities: [],
      selectedProducts: null,
      filters: {},
      isFactoryTeamMember: false,
      oldData: {},
      isProductionTeamMember: false,
      isFinanceTeamMember: false,
      submitted: false,
      isEditEntryDialoge: false,
      vendorName: '',
      userInfo: {},
      allLiabilityData: {},
      liabilityClosedFlag: null,
      enableSaveClonedCTA: false,
      totalLiabilityCount: 0,
      currentDate: new Date().getMonth() > 8 ? (new Date().getMonth() + 1) : ('0' + (new Date().getMonth() + 1)) + '-' + ((new Date().getDate() > 9) ? new Date().getDate() : ('0' + new Date().getDate())) + '-' + new Date().getFullYear(),
      apiUrlPath: 'api/master-liability-chart',
      isValidShelfLife: false,
      isValidInvoicePaid: false,
      validDate: false,
      skeletonData: [
        {
          date_incurred:'',
          vendor:'',
          liability_reason:'',
          status:''
        },
        {
          date_incurred:'',
          vendor:'',
          liability_reason:'',
          status:''
        },
        {
          date_incurred:'',
          vendor:'',
          liability_reason:'',
          status:''
        },
        {
          date_incurred:'',
          vendor:'',
          liability_reason:'',
          status:''
        },
        {
          date_incurred:'',
          vendor:'',
          liability_reason:'',
          status:''
        },
        {
          date_incurred:'',
          vendor:'',
          liability_reason:'',
          status:''
        }
      ],
      liabilityStatus: [
        {name: 'CHECK WITH VENDOR', value: 'CHECK WITH VENDOR'},
        {name: 'NEED USAGE PLAN', value: 'NEED USAGE PLAN'},
        {name: 'INVOICE RECEIVED', value: 'INVOICE RECEIVED'},
        {name: 'PAID AS ADVANCE INTEND TO USE', value: 'PAID AS ADVANCE INTEND TO USE'},
        {name: 'EXPIRED / NEED TO SETTLE', value: 'EXPIRED / NEED TO SETTLE'},
        {name: 'LIABILITY USED/ CLOSED', value: 'LIABILITY USED/ CLOSED'},
        {name: 'VENDORS LIABILITY', value: 'VENDORS LIABILITY'},
        {name: 'CONFIRMED TO WRITE OFF', value: 'CONFIRMED TO WRITE OFF'},
      ],
      liabilitySeasonName: [
        {name: 'Fall', value: 'Fall'},
        {name: 'Holiday', value: 'Holiday'},
        {name: 'Spring', value: 'Spring'},
        {name: 'Summer', value: 'Summer'},
        {name: 'Basic', value: 'Basic'}
      ],
      liabilityColorPattern: [
        {name: 'Greige', value: 'Greige'},
        {name: 'Dye', value: 'Dye'},
        {name: 'Not Applicable', value: 'Not Applicable'},
      ],
      liabilityLocation: [
        {name: 'Factory', value: 'Factory'},
        {name: 'Mill', value: 'Mill'}
      ],
      liabilityUOM: [
        {name: 'PCS', value: 'PCS'},
        {name: 'UNITS', value: 'UNITS'},
        {name: 'YDS', value: 'YDS'},
        {name: 'MTRS', value: 'MTRS'},
        {name: 'CONES', value: 'CONES'},
        {name: 'EACH', value: 'EACH'},
        {name: 'SET', value: 'SET'},
      ],
      liabilityDeposit: [
        {name: 'Yes', value: 'Yes'},
        {name: 'No', value: 'No'}
      ],
      approvedOptions: [
        {name: 'Yes', value: 'Yes'},
        {name: 'No', value: 'No'}
      ],
      liabilityInvoiceApprover: [
        {name: 'Design', value: 'Design'},
        {name: 'Merch', value: 'Merch'},
        {name: 'Marketing', value: 'Marketing'},
        {name: 'ELT', value: 'ELT'}
      ],
      liabilityClosedStatus: [
        {name: 'Yes', value: 'Yes'},
        {name: 'No', value: 'No'}
      ],
      liabilityReason: [
        {name: 'Production (Change allocation)', value: 'Production (Change allocation)'},
        {name: 'Planning/Merch (Cancelled PO/Reduced Units/MOQ/Asst Changes)', value: 'Planning/Merch (Cancelled PO/Reduced Units/MOQ/Asst Changes)'},
        {name: 'Design (Product Change/Fit Change)', value: 'Design (Product Change/Fit Change)'},
        {name: 'Marketing (GWP)', value: 'Marketing (GWP)'},
        {name: 'ELT decision', value: 'ELT decision'},
        {name: 'Contingency', value: 'Contingency'},
        {name: 'Used or Destroy', value: 'Used or Destroy'},
      ],
      divisionOptions: [
        {name: 'Men', value: 'Men'},
        {name: 'Women', value: 'Women'},
        {name: 'Not Applicable', value: 'Not Applicable'},
      ],
    }
  },

  mounted() {
    this.getVendorName();
    this.retrieveLiabilities();
    sessionStorage.removeItem("liabilityID");
  },

  methods: {
    liabilityClosedStatusChanged (event) {
      if (event.value == 'Yes') {
        this.liabilityClosedFlag = true;
        this.masterLiabilityInfo.date_closed = this.currentDate;
      } else {
        this.liabilityClosedFlag = false;
        this.masterLiabilityInfo.date_closed = null;
      }
    },

    rowClass (data) {
      return data.entryStatus === 'Cloned' ? 'cloned-liabilities-row': null;
    },

    populateCommentMarkup (liabilityComments) {
      let $commentParentDiv = $('<p>');
      liabilityComments.forEach(liabilityComment => { 
        let date = moment(liabilityComment.createdAt).format("MM-DD-YYYY | hh:mm:ss a");
        let comment = liabilityComment.comment_content;
        let userName = liabilityComment.user_name;
        let userInitials = userName.match(/\b(\w)/g).join('');
        let $commentDiv = $(`<p><span class="comment-info">${userInitials} | ${date} | </span><span class="comment-content">${comment}</span></p>`);
        $commentParentDiv.append($commentDiv);
        liabilityComment.comment = 1;
      });
      return $commentParentDiv[0].innerHTML;
    },

    getVendorName () {
      if (sessionStorage.getItem("User Information")) {
        this.userInfo = JSON.parse(sessionStorage.getItem("User Information"));
        this.vendorName = this.userInfo.vendor_name;
        if (this.userInfo.team == "Factory Team") {
          this.isFactoryTeamMember = true;
        } else if (this.userInfo.team == "Production Team") {
          this.isProductionTeamMember = true;
        } else if (this.userInfo.team == "Finance Team") {
          this.isFinanceTeamMember = true;
        }
      }
    },

    bulkEditClickHandler () {
      sessionStorage.setItem("liabilityID", this.selectedProducts.map(o => o.id));
      this.$router.push({ name: "BulkUpdteLiability", params: { liabilityRowIDs: this.selectedProducts.map(o => o.id)} });
    },

    saveClonedLiabilities () {
       http
      .post(`${this.apiUrlPath}/save-cloned-liability`,this.clonedLiabilities)
      .then((response) => {
        if (response.status == 200) {
          this.$toast.add({severity:'success', summary: 'Successful', detail: 'Cloned Liability Rows were Saved Successfully', life: 3000});
          this.retrieveLiabilities();
          this.enableSaveClonedCTA = false;
        }
      })
      .catch((e) => {
        this.$toast.add({severity:'error', summary: 'Error', detail: "Couldn't Save Cloned Liability Rows", life: 3000});
        console.log(e);
      });
    },

    onFileUpload (event) {
      this.file = this.$refs.file.files[0];
      let formData = new FormData();
      formData.append('file', this.file);

      if(this.file.type !== 'text/csv' && this.file.type !== 'application/vnd.ms-excel') {
        // Windows returns file type of CSV as application/vnd.ms-excel
        this.$toast.add({severity:'error', summary: 'Error', detail: "Please upload a CSV file only", life: 5000});
        this.$refs.file.files.pop();
      } else {
        http.post(`${this.apiUrlPath}/uploadfile`,formData,
          { headers: { 'Content-Type': 'multipart/form-data' } })
          .then((response) => {
            if(!response.data.includes("Please make sure")) {
              this.$toast.add({severity:'success', summary: 'Success', detail: response.data, life: 10000});
              this.retrieveLiabilities()
              this.$refs.file.files.pop();
            } else {
              this.$toast.add({severity:'error', summary: 'Error', detail: response.data, life: 5000});
              this.$refs.file.files.pop();
              this.retrieveLiabilities();
            }
          })
          .catch(function(err){
            console.log('FAILURE!!', err);
        });
      }
    },

    cloneRowsHandler () {
      this.selectedProducts.forEach(element => {
        let clonedElement = Object.assign({}, element)
        clonedElement.id = Math.floor(Math.random() * 1000000000);
        clonedElement.date_incurred = this.currentDate;
        clonedElement.comment = [];
        clonedElement.status = "New Adds";
        clonedElement.closed = "No";
        clonedElement.date_closed = clonedElement.date_invoice_received = clonedElement.invoice = clonedElement.initial_approver_for_invoice_payment = clonedElement.final_approver_for_invoice_payment = clonedElement.date_invoice_paid = null;
        clonedElement.entryStatus = "Cloned"
        this.masterLiabilityData.push(clonedElement);
        this.clonedLiabilities.push(clonedElement);
      });
      this.selectedProducts = [];
      this.enableSaveClonedCTA = true;
    },

    isProductionTeam () {
      return false;
    },

    isFactoryTeam () {
      return true;
    },

    isFinanceTeam () {
      return false;
    },

    getDefaultValues () {
      this.masterLiabilityInfo.vendor = this.vendorName;
      this.masterLiabilityInfo.date_incurred = this.currentDate;
      this.masterLiabilityInfo.status = "";
      this.masterLiabilityInfo.closed = "No";
      this.masterLiabilityInfo.liability_reason = ""
    },

    handleLiabilityComment (id) {
      return this.masterLiabilityCommentData.filter(data => data.liability_id == id)
    },

    retrieveLiabilities() {
      let masterLiabilityData = this.masterLiabilityData;
      http
      .get(`${this.apiUrlPath}/master-liability-chart-entries/${this.vendorName}`)
      .then((response) => {
        masterLiabilityData = response.data; // JSON are parsed automatically.
        this.totalLiabilityCount = masterLiabilityData.length;
        http
      .get(`${this.apiUrlPath}/master-liability-comments/${this.vendorName}`)
      .then((response) => {
        this.masterLiabilityCommentData = response.data; // JSON are parsed automatically.
        masterLiabilityData.forEach((element,index) => {
          element.comment = this.handleLiabilityComment(element.id);
          element.liability_comment_data = {};
          if(index ==  masterLiabilityData.length - 1) {
            this.isLoaded = true;
            this.masterLiabilityData = masterLiabilityData;
          }
        });  
      })
      .catch((e) => {
        console.log(e);
      });
      })
      .catch((e) => {
        console.log(e);
      });
    },

    showHistory (data) {
      let editData;
      http
      .get(`${this.apiUrlPath}/editLog/${data.id}`)
      .then((response) => {
        editData = response.data;
        this.$router.push({ name: "EditLog", params: {editLogInfo: editData} });
      })
      .catch((e) => {
        console.log(e);
      });

    },
        
    openNew() {
      this.masterLiabilityInfo = {};
      this.getDefaultValues ();
      this.submitted = false;
      this.productDialog = true;
      this.isEditEntryDialoge = false;
    },
        
    hideDialog() {
      this.productDialog = false;
      this.submitted = false;
    },
        
    saveProduct() {
      this.submitted = true;
      let isAllFactoryFieldsValid = this.checkAllFactoryFields ();
      if (isAllFactoryFieldsValid) {
        if (this.masterLiabilityInfo.id) {
          let allLiabilityData = this.oldData;
          this.masterLiabilityInfo.total_liability = (this.masterLiabilityInfo.quantity * this.masterLiabilityInfo.fob_unit_cost).toFixed(2);
          if (this.masterLiabilityInfo.entryStatus && this.masterLiabilityInfo.entryStatus == 'Cloned') {
            this.$set(this.masterLiabilityData, this.findIndexById(this.masterLiabilityInfo.id), this.masterLiabilityInfo);
            this.$set(this.clonedLiabilities, this.findIndexOfClonedLiabilityById(this.masterLiabilityInfo.id), this.masterLiabilityInfo);
          } else {
            this.masterLiabilityInfo.entryStatus = "Updated"
            this.masterLiabilityInfo.liability_comment_data.comment_content = $('#liability_comment').val();
            this.masterLiabilityInfo.liability_comment_data.user_name = this.userInfo.username;
            this.masterLiabilityInfo.liability_comment_data.factory_name = this.userInfo.vendor_name;
            this.masterLiabilityInfo.liability_comment_data.liability_id = this.masterLiabilityInfo.id;
            this.masterLiabilityInfo.date_closed = this.liabilityClosedFlag ? this.currentDate : null;
            $('#liability_comment').val('');

            this.oldData.masterLiabilityInfo = this.masterLiabilityInfo
            this.oldData.userInfo = JSON.parse(sessionStorage.getItem("User Information"));
            let backup = this;
            http
            .put(`${backup.apiUrlPath}/update-liability`,backup.oldData)
              .then(function(response) {
              if (response.status == 200 && response.data.message == "Record Saved Successfully") {
                backup.retrieveLiabilities()
                backup.$toast.add({severity:'success', summary: 'Successful', detail: response.data.message, life: 3000});
              } else {
                backup.retrieveLiabilities()
                backup.$toast.add({severity:'error', summary: 'Error', detail: response.data.message, life: 3000});
              }
            })
            .catch(function(e) {
              backup.$toast.add({severity:'error', summary: 'Error', detail: 'Could\'t Save the Record. Please check the data', life: 3000});
            });
  
            backup.$set(this.masterLiabilityData, this.findIndexById(this.masterLiabilityInfo.id), this.masterLiabilityInfo);
          }
        } else {
          this.masterLiabilityInfo.total_liability = (this.masterLiabilityInfo.quantity * this.masterLiabilityInfo.fob_unit_cost).toFixed(2);
          this.masterLiabilityInfo.id = this.createId();
          this.masterLiabilityInfo.entryStatus = "New"
           http
            .post(`${this.apiUrlPath}/create-new-liability`,this.masterLiabilityInfo)
              .then((response) => {
                if (response.status == 200) {
                  this.retrieveLiabilities()
                  this.$toast.add({severity:'success', summary: 'Successful', detail: 'Liability Record Created', life: 3000});
                }
              })
            .catch((e) => {
                this.$toast.add({severity:'error', summary: 'Error', detail: 'Unable to create the Record', life: 3000});
          });
        }
        this.productDialog = false;
        this.masterLiabilityInfo = {};
      }
    },

    editProduct(masterLiabilityInfo) {
      this.masterLiabilityInfo = {...masterLiabilityInfo};
      this.oldData.unupdated = {...masterLiabilityInfo};
      this.productDialog = true;
      this.isEditEntryDialoge = true;
    },

    findIndexOfClonedLiabilityById(id) {
      let index = -1;
      for (let i = 0; i < this.clonedLiabilities.length; i++) {
        if (this.clonedLiabilities[i].id === id) {
          index = i;
          break;
        }
      }
      return index;
    },

    findIndexById(id) {
      let index = -1;
      for (let i = 0; i < this.masterLiabilityData.length; i++) {
        if (this.masterLiabilityData[i].id === id) {
          index = i;
          break;
        }
      }
      return index;
    },

    createId() {
      let id = '';
      var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      for ( var i = 0; i < 5; i++ ) {
        id += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return id;
    },

    exportCSV() {
      let localmasterLiabilityData = this.masterLiabilityData;
      this.masterLiabilityData.forEach(liabilityData => {
        liabilityData.comment = [];
      });
      this.$refs.dt.exportCSV();
      this.masterLiabilityData = localmasterLiabilityData;
    },

    checkAllFactoryFields () {
      if (this.masterLiabilityInfo.shelf_life) {
        if (moment(this.masterLiabilityInfo.shelf_life, "MM-DD-YYYY", true).isValid()) {
          this.isValidShelfLife = true
          this.validDate = true
        } else {
          this.isValidShelfLife = false
          this.validDate = false
        }
      } else {
        this.isValidShelfLife = false
        this.validDate = false
      }
      if (this.masterLiabilityInfo.date_invoice_paid) {
        if (moment(this.masterLiabilityInfo.date_invoice_paid, "MM-DD-YYYY", true).isValid()) {
          this.isValidInvoicePaid = true
        } else {
          this.isValidInvoicePaid = false
        }
      }
      if(this.masterLiabilityInfo.vendor && this.masterLiabilityInfo.division && this.masterLiabilityInfo.season_name && this.masterLiabilityInfo.season_year &&
      this.masterLiabilityInfo.supplier && this.masterLiabilityInfo.collection && this.masterLiabilityInfo.greige_or_dyed && this.masterLiabilityInfo.liability_location &&
      this.masterLiabilityInfo.article && this.masterLiabilityInfo.color && this.masterLiabilityInfo.quantity && this.masterLiabilityInfo.uom &&
      this.masterLiabilityInfo.fob_unit_cost && this.isValidShelfLife && this.masterLiabilityInfo.estimated_garment_units &&
      this.masterLiabilityInfo.deposit && this.masterLiabilityInfo.po && this.masterLiabilityInfo.original_units && this.masterLiabilityInfo.original_total_liability) {
      return true;
    } else {
      return false;
    }
  },

    onUpload() {
			this.$toast.add({severity: 'info', summary: 'Success', detail: 'File Uploaded', life: 3000});
		}
  }
};
</script>

<style>
.custom-dialog .p-inputtext {
  padding: 14px 10px;
  font-size: 16px;
}

.liability-chart.p-inputtext,
.liability-chart.p-dropdown {
  width: 100%;
}

.liability-chart .p-inputtext {
  font-size: 14px;
}
.comment-content {
  color: #00424a;
  font-weight: 700;
}
.hidden {
  display: none;
}
.data-table-parent-container  {
  overflow: hidden;
}

.data-table-parent-container .p-datatable-scrollable-header {
  overflow: unset;
}

.liability-chart.p-datatable .multi-option {
  color: #4d4d4d !important;
  width: 100%;
}
.liability-chart  .p-datatable-tbody > tr.cloned-liabilities-row {
  background: #4d4d4d;
}

.liability-chart .p-datatable-tbody > tr.cloned-liabilities-row > td {
  color: #fff;
}

.liability-count {
  color: #4d4d4d;
  font-size: 12px;
  margin-top: 4px;
}

.data-table-parent-container .p-paginator-bottom {
  position: fixed;
  bottom: 0;
  width: 100%;
}

.data-table-parent-container .p-datatable table {
  margin-bottom: 14px;
}

.data-table-parent-container .p-datatable .p-datatable-scrollable-footer {
  background: unset;
}

.tool-bar-styling.p-toolbar {
  background: #f5f5f5;
  border: 1px solid #00424a;
  padding: 1rem;
  margin: 1rem 0;
}

.tool-bar-styling .p-button-info.p-button, .tool-bar-styling .p-button, .tool-bar-styling .p-button.p-button-help {
  background: #00424a;
  border: 1px solid #00424a;
  color: #fff;
}

.tool-bar-styling .p-button-info.p-button:enabled:hover, .tool-bar-styling .p-button.p-fileupload-choose:hover, .tool-bar-styling .p-button.p-button-help:enabled:hover {
  background: #00424a;
  border: 1px solid #00424a;
  color: #fff;
}
.p-datatable-scrollable-body {
  margin-bottom: 2rem;
  min-height: 20vh;
}
.tool-bar-styling .p-button-info.p-button:disabled{
  background: #fff;
  border: 1px solid #333;
  color: #000;
  cursor: not-allowed;
}
</style>